import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Undo2, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export default function PortalMessagesAdmin() {
  const [filter, setFilter] = useState("pending");
  const [messages, setMessages] = useState([]);

  const load = async (f = filter) => {
    try {
      const { data } = await api.get(`/admin/client-messages?status=${f}`);
      setMessages(data);
    } catch {}
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const toggle = async (m) => {
    try {
      await api.put(`/admin/client-messages/${m.id}`, { answered: !m.answered });
      toast.success(m.answered ? "Marcado como sin contestar" : "Marcado como contestado");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar mensaje de ${m.client_name}?`)) return;
    try {
      await api.delete(`/admin/client-messages/${m.id}`);
      toast.success("Mensaje eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error");
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-6" data-testid="portal-msg-filter">
        {[
          { k: "pending", label: "Sin contestar" },
          { k: "answered", label: "Contestadas" },
          { k: "all", label: "Todas" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`portal-msg-filter-${f.k}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${
              filter === f.k
                ? "bg-[#D4AF37]/10 border-[#D4AF37]/50 text-[#D4AF37]"
                : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto text-xs text-zinc-600 self-center">
          Mensajes que envían tus clientes desde su área privada
        </div>
      </div>

      <div className="space-y-4">
        {messages.map((m) => {
          return (
            <div key={m.id} className={`bg-[#121214] border rounded-xl p-6 transition-colors ${m.answered ? "border-white/5 opacity-70" : "border-white/10"}`} data-testid={`portal-msg-${m.id}`}>
              <div className="flex justify-between items-start mb-3 gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-white">{m.client_name}</div>
                    {m.client_company && <span className="text-xs text-zinc-500">· {m.client_company}</span>}
                    {m.answered ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400">Contestado</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37]">Pendiente</span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-500 mt-1">{m.client_email}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-zinc-500 font-mono">
                    {new Date(m.created_at).toLocaleString("es-ES")}
                  </div>
                </div>
              </div>
              <div className="text-[#D4AF37] text-sm font-medium mb-2">{m.subject}</div>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
              {(m.notification_delivered_to?.length || m.notification_failed_to?.length) ? (
                <div className="mt-3 text-[11px] font-mono text-zinc-500 flex flex-wrap gap-x-3 gap-y-1">
                  {m.notification_delivered_to?.length > 0 && (
                    <span data-testid={`portal-msg-delivered-${m.id}`} className="text-green-500/80">✓ email → {m.notification_delivered_to.join(", ")}</span>
                  )}
                  {m.notification_failed_to?.length > 0 && (
                    <span data-testid={`portal-msg-failed-${m.id}`} className="text-red-400/80">✕ falló → {m.notification_failed_to.join(", ")}</span>
                  )}
                </div>
              ) : null}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/5">
                <button
                  onClick={() => toggle(m)}
                  data-testid={`portal-msg-toggle-${m.id}`}
                  className={`text-xs px-4 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${
                    m.answered
                      ? "border-white/10 text-zinc-400 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
                      : "border-green-500/40 text-green-400 hover:bg-green-500/10"
                  }`}
                >
                  {m.answered ? (<><Undo2 className="w-3.5 h-3.5" /> Marcar sin contestar</>) : (<><Check className="w-3.5 h-3.5" /> Marcar contestado</>)}
                </button>
                <a
                  href={`mailto:${m.client_email}?subject=Re:%20${encodeURIComponent(m.subject)}`}
                  className="text-xs px-4 py-1.5 rounded-full border border-white/10 text-zinc-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors"
                >
                  Responder por email
                </a>
                <button
                  onClick={() => remove(m)}
                  data-testid={`portal-msg-delete-${m.id}`}
                  className="ml-auto text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-500 hover:border-red-500/50 hover:text-red-400 transition-colors flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              </div>
            </div>
          );
        })}
        {!messages.length && (
          <div className="text-center py-16 text-zinc-500">
            {filter === "pending" && "No hay mensajes pendientes de contestar de tus clientes."}
            {filter === "answered" && "Aún no has marcado ningún mensaje como contestado."}
            {filter === "all" && "Aún no hay mensajes de clientes desde su área privada."}
          </div>
        )}
      </div>
    </div>
  );
}
